/**
 * Importação do protocolo de assinatura (E77.F1, ADR-0078).
 *
 * Módulo próprio, e não mais uma seção de `exame_eletronico.ts`: aquele arquivo
 * já estava no teto de 400 linhas do lint, e a importação é uma jornada
 * distinta da verificação — sobe um documento, lê os campos e preenche o caso.
 *
 * A regra que atravessa o módulo desde a E84.F1 (ADR-0078 §2.2 emendada): a
 * importação **preenche só o que está vazio** — o protocolo do caso se ainda não
 * informado, e o código e o tipo das peças casadas pelo hash — e **preserva a
 * edição humana**; o que gravou volta em `reconciliacao`. A sugestão com
 * proveniência continua na resposta para o perito conferir e editar por cima
 * (`salvarProtocoloDoCaso`, `salvarCodigoDaPeca`). Só o passo do resíduo
 * (`lerResiduoDoProtocolo`) não grava nada.
 */
import type { AxiosInstance } from 'axios';

import {
  classifyExameEletronicoError,
  ExameEletronicoError,
  type ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico';

function basePath(caseId: string): string {
  return `/casos/${caseId}/exame-eletronico`;
}

/** De onde veio um campo sugerido: lido por rótulo, ou pela leitura assistida. */
export type ProvenienciaCampo = 'regex' | 'assistida' | 'leitura_por_imagem';

export interface AnexoDoProtocoloData {
  nome_arquivo: string;
  content_sha256: string;
  importado_em: string;
}

export interface ProtocoloSugeridoData extends ProtocoloAssinaturaCasoData {
  proveniencia: Record<string, ProvenienciaCampo>;
}

/** Um SHA-256 impresso no protocolo, com o rótulo que o acompanha (E83.F1). */
export interface CodigoDeclaradoLidoData {
  codigo: string;
  rotulo: string | null;
}

/**
 * Um código do protocolo casado com uma peça do caso pelo hash apurado
 * (E83.F1, ADR-0078 §2.7): igualdade byte a byte com o `content_sha256` da
 * ingestão. Desde a E84.F1 o casado é GRAVADO na peça quando o código dela está
 * vazio (`reconciliacao.gravados`); peça com código já informado é preservada
 * (`reconciliacao.ja_preenchidos`) e o perito edita pelo `salvarCodigoDaPeca`.
 */
export interface CodigoPorPecaSugeridoData {
  source_document_id: string;
  codigo: string;
  rotulo: string | null;
}

/** O que a importação GRAVOU nas peças (E84.F1): só o que estava vazio. */
export interface ResumoReconciliacaoData {
  gravados: string[];
  ja_preenchidos: string[];
  sem_peca: CodigoDeclaradoLidoData[];
  pecas_sem_codigo: string[];
  pulados_concluidos: string[];
}

/** Um código persistido do comprovante e a peça com que ele casa hoje (E84.F1). */
export interface CodigoDeclaradoDoCasoData extends CodigoDeclaradoLidoData {
  /** Nulo = nenhuma peça viva tem esse hash — "código sem peça". */
  source_document_id: string | null;
}

/** Resposta de `GET /protocolo` desde a E84.F1: o protocolo mais os códigos. */
export interface ProtocoloDoCasoData extends ProtocoloAssinaturaCasoData {
  codigos_declarados: CodigoDeclaradoDoCasoData[];
}

/**
 * O protocolo do caso com os códigos do comprovante (E84.F1); `null` enquanto
 * o protocolo não foi informado — o 404 da rota é estado, não erro.
 */
export async function lerProtocoloDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<ProtocoloDoCasoData | null> {
  try {
    const { data } = await client.get<ProtocoloDoCasoData>(`${basePath(caseId)}/protocolo`);
    return data;
  } catch (err) {
    const classificado = classifyExameEletronicoError(err);
    if (classificado.reason === 'not_found') return null;
    throw classificado;
  }
}

export interface ProtocoloImportadoData {
  sugestao: ProtocoloSugeridoData;
  anexo: AnexoDoProtocoloData;
  /** SHA-256 da PEÇA — gravado pela rota do código, não pelo PUT do protocolo. */
  codigo_declarado_sugerido: string | null;
  /** TODOS os SHA-256 lidos do protocolo, na ordem do texto (E83.F1). */
  codigos_declarados: CodigoDeclaradoLidoData[];
  /** Os que casaram com uma peça do caso pelo hash apurado. */
  codigos_por_peca: CodigoPorPecaSugeridoData[];
  /** Declarados no protocolo e sem peça de hash igual — achado, não ruído. */
  codigos_sem_peca: CodigoDeclaradoLidoData[];
  /** Peças do caso que nenhum código do protocolo cobre. */
  pecas_sem_codigo: string[];
  /** E84.F1 — o que foi gravado; `null` no passo do resíduo, que não grava. */
  reconciliacao?: ResumoReconciliacaoData | null;
  /** Sobrou campo E vale oferecer a leitura assistida (que é paga). */
  residuo_disponivel: boolean;
  /** Por que a leitura assistida não completou, quando foi tentada. */
  leitura_falhou: string | null;
}

/**
 * Sobe o PDF do protocolo, recebe a sugestão do passo determinístico e o que
 * foi gravado.
 *
 * **Grava o que está vazio** (E84.F1, ADR-0078 §2.2 emendada): o protocolo do
 * caso se ainda não informado, e código + tipo das peças casadas sem código.
 * O que o perito já informou não é tocado; `reconciliacao` diz o que entrou.
 * A tela recarrega depois desta chamada para mostrar o gravado.
 */
export async function importarProtocolo(
  client: AxiosInstance,
  caseId: string,
  arquivo: File,
): Promise<ProtocoloImportadoData> {
  const form = new FormData();
  form.append('file', arquivo);
  try {
    const { data } = await client.post<ProtocoloImportadoData>(
      `${basePath(caseId)}/protocolo/importar`,
      form,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Leitura assistida do que a extração determinística não encontrou.
 *
 * Ação paga e opt-in (§2.4). Falha do provedor NÃO levanta: volta em
 * `leitura_falhou`, e o formulário manual segue aberto (§2.6).
 */
export async function lerResiduoDoProtocolo(
  client: AxiosInstance,
  caseId: string,
): Promise<ProtocoloImportadoData> {
  try {
    const { data } = await client.post<ProtocoloImportadoData>(
      `${basePath(caseId)}/protocolo/importar/residuo`,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * E101.F5 — a sugestão dos comprovantes lidos por imagem (ADR-0085 §2.4), recalculada pelo
 * servidor do texto guardado. Nada é gravado: o "Salvar" do perito confirma.
 */
export interface ProtocoloPorImagemData {
  sugestao: ProtocoloSugeridoData | null;
  documentos: string[];
  /** Algum comprovante do caso ainda aguarda a leitura ou está sendo lido. */
  leitura_em_andamento: boolean;
}

export async function lerSugestaoPorImagem(
  client: AxiosInstance,
  caseId: string,
): Promise<ProtocoloPorImagemData> {
  let dados: unknown;
  try {
    ({ data: dados } = await client.get<ProtocoloPorImagemData>(
      `${basePath(caseId)}/protocolo/sugestao-por-imagem`,
    ));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  const corpo = dados as Partial<ProtocoloPorImagemData> | null;
  if (typeof corpo?.leitura_em_andamento !== 'boolean' || !Array.isArray(corpo.documentos)) {
    throw new ExameEletronicoError('unknown');
  }
  return corpo as ProtocoloPorImagemData;
}

/** O protocolo sugerido na forma do formulário: a proveniência informa a tela e não se grava. */
export function protocoloDaSugestao(sugestao: ProtocoloSugeridoData): ProtocoloAssinaturaCasoData {
  return Object.fromEntries(
    Object.entries(sugestao).filter(([chave]) => chave !== 'proveniencia'),
  ) as unknown as ProtocoloAssinaturaCasoData;
}
